<template>
  <div class="acts-wrapper">
    <h2 class="section-title">{{ applicationInfo.student_name }}</h2>

    <div class="acts-inner-wrapper">
      <!-- Левая часть (Детальная информация) -->
      <div class="acts-detail">
        <h3 class="section-subtitle">
          {{ $t(`applications.applicationDetail.applicationNumber`) }}
          {{ applicationInfo.id }}
        </h3>

        <v-card class="info-card">
          <v-simple-table>
            <tbody>
              <tr>
                <td>{{ $t(`applications.applicationDetail.status`) }}</td>
                <td>{{ applicationInfo.status_name }}</td>
              </tr>
              <tr v-if="applicationInfo.status_id == 3">
                <td>{{ $t(`applications.applicationDetail.reason`) }}</td>
                <td>{{ applicationInfo.comment }}</td>
              </tr>
              <tr>
                <td>
                  {{ $t(`applications.applicationDetail.birthCertificate`) }}
                </td>
                <td>
                  <v-btn
                    text
                    small
                    @click="downloadFile(applicationInfo.certificate_file_path, 'birthCertificate')"
                    :disabled="isFileLoading('birthCertificate')"
                    class="file-btn"
                  >
                    <v-icon
                      v-if="isFileLoading('birthCertificate')"
                      small
                      class="mr-1"
                    >
                      mdi-loading mdi-spin
                    </v-icon>
                    {{
                      $t(`applications.applicationDetail.downloadFile`)
                    }}
                  </v-btn>
                </td>
              </tr>
              <tr>
                <td>
                  {{ $t(`applications.applicationDetail.medicalCertificate`) }}
                </td>
                <td>
                  <v-btn
                    text
                    small
                    @click="downloadFile(applicationInfo.medical_certificate_file_path, 'medicalCertificate')"
                    :disabled="isFileLoading('medicalCertificate')"
                    class="file-btn"
                  >
                    <v-icon
                      v-if="isFileLoading('medicalCertificate')"
                      small
                      class="mr-1"
                    >
                      mdi-loading mdi-spin
                    </v-icon>
                    {{
                      $t(`applications.applicationDetail.downloadFile`)
                    }}
                  </v-btn>
                </td>
              </tr>
              <tr>
                <td>{{ $t(`applications.applicationDetail.category`) }}</td>
                <td>{{ applicationInfo.social_category_name }}</td>
              </tr>
              <tr v-if="applicationInfo.social_category_id != 12">
                <td>{{ $t(`applications.applicationDetail.socialCategoryDocument`) }}</td>
                <td>
                  <v-btn
                    text
                    small
                    @click="downloadFile(applicationInfo.social_category_document_file_path, 'socialDocument')"
                    :disabled="isFileLoading('socialDocument')"
                    class="file-btn"
                  >
                    <v-icon
                      v-if="isFileLoading('socialDocument')"
                      small
                      class="mr-1"
                    >
                      mdi-loading mdi-spin
                    </v-icon>
                    {{
                      $t(`applications.applicationDetail.downloadFile`)
                    }}
                  </v-btn>
                </td>
              </tr>
              <tr>
                <td>{{ $t(`applications.applicationDetail.statement`) }}</td>
                <td>
                  <v-btn
                    text
                    small
                    @click="downloadFile(applicationInfo.file_path, 'mainDocument')"
                    :disabled="isFileLoading('mainDocument')"
                    class="file-btn"
                  >
                    <v-icon
                      v-if="isFileLoading('mainDocument')"
                      small
                      class="mr-1"
                    >
                      mdi-loading mdi-spin
                    </v-icon>
                    {{
                      $t(`applications.applicationDetail.downloadFile`)
                    }}
                  </v-btn>
                </td>
              </tr>
              <tr>
                <td>{{ $t(`applications.applicationDetail.submitted`) }}</td>
                <td>{{ formatDateToMonthYear(applicationInfo.created_at) }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card>
      </div>

      <!-- Правая часть -->
      <div class="acts-list">
        <div class="price-card">
          <h3 class="group-name-title">{{ formattedName }}</h3>
          <h3 v-if="applicationInfo.sub_group_name" class="sub-group-name-title">{{ $t("applications.applicationDetail.subGroupName") }}: {{ applicationInfo.sub_group_name }}</h3>
          <v-divider></v-divider>
          
          <v-btn
            v-if="Number(applicationInfo.status_id) === 1"
            class="btn accept-btn"
            @click="confirmApplicationAction('delete')"
            :disabled="isSubmitLoading"
          >
            {{ $t("applications.applicationDetail.deleteApplication") }}
          </v-btn>

          <v-btn
            v-else-if="Number(applicationInfo.status_id) === 2"
            class="btn accept-btn"
            @click="confirmApplicationAction('withdraw')"
            :disabled="isSubmitLoading"
          >
            {{ $t("applications.applicationDetail.withdrawApplication") }}
          </v-btn>

          <v-btn
            text
            small
            class="share-btn"
            @click="$router.push('/courses/circles/' + applicationInfo.groupe_id)"
          >
            {{ $t("applications.applicationDetail.toCourse") }}
          </v-btn>
        </div>
      </div>
    </div>
    
    <ProgressCircular
      :isSubmitLoading="isSubmitLoading"
      v-if="isLoading || isSubmitLoading"
    />
    
    <v-dialog v-model="confirmDialog.visible" max-width="500" persistent>
      <v-card>
        <v-card-title class="headline">
          {{ $t("applications.applicationDetail.confirmTitle") }}
        </v-card-title>

        <v-card-text>
          <p>
            {{
              confirmDialog.action === "withdraw"
                ? $t("applications.applicationDetail.confirmWithdrawText")
                : $t("applications.applicationDetail.confirmDeleteText")
            }}
          </p>
        </v-card-text>

        <v-card-actions class="justify-end">
          <v-btn text @click="confirmDialog.visible = false">
            {{ $t("applications.applicationDetail.cancel") }}
          </v-btn>
          <v-btn color="primary" @click="handleConfirm">
            {{ $t("applications.applicationDetail.confirm") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ProgressCircular from "@/components/custom/ProgressCircular.vue";
import { format, parseISO } from "date-fns";
import { showToast } from "@/utils/toast";
import { fetchDeleteApplication } from "@/api/profile";
import { getFile } from "@/api/landing";

export default {
  name: "ApplicationsDetail",
  components: { ProgressCircular },
  data() {
    return {
      isLoading: false,
      isSubmitLoading: false,
      confirmDialog: {
        visible: false,
        action: null,
      },
      // Объект для отслеживания загрузки файлов по типам
      fileLoading: {
        birthCertificate: false,
        medicalCertificate: false,
        socialDocument: false,
        mainDocument: false,
      }
    };
  },
  created() {
    this.initializePageData();
  },
  computed: {
    ...mapGetters("applications", ["applicationInfo"]),

    formattedName() {
      const name = this.applicationInfo.group_name
      if (!name) return '';

      if (!/^[a-zA-Zа-яА-Я]/.test(name[0])) {
        return name;
      }

      return name.charAt(0).toUpperCase() + name.slice(1);
    }
  },
  methods: {
    ...mapActions({
      fetchApplicationInfo: "applications/loadApplicationInfo",
    }),
    
    /**
     * Загрузка и открытие файла
     */
    async downloadFile(filePath, fileType) {
      // Проверяем, не загружается ли уже файл
      if (this.isFileLoading(fileType) || !filePath) {
        if (!filePath) {
          showToast(this, {
            text: this.$t("applications.applicationDetail.fileNotFound"),
            status: "warning",
            duration: 3000,
          });
        }
        return;
      }

      // Устанавливаем состояние загрузки
      this.$set(this.fileLoading, fileType, true);

      try {
        const response = await getFile(filePath);
        
        if (response.data?.success && response.data.base64) {
          const fileName = this.extractFileName(
            response.data.filename || filePath
          );
          const mimeType = response.data.mime_type || 'application/pdf';
          
          this.openFileInNewTab(
            response.data.base64,
            fileName,
            mimeType
          );
          
          showToast(this, {
            text: this.$t("applications.applicationDetail.fileOpened"),
            status: "info",
            duration: 2000,
          });
        } else {
          throw new Error(response.data?.error || 'Файл не найден');
        }
      } catch (error) {
        console.error('Ошибка при загрузке файла:', error);
        this.handleFileError(error);
      } finally {
        this.$set(this.fileLoading, fileType, false);
      }
    },

    /**
     * Открытие файла в новой вкладке
     */
    openFileInNewTab(base64String, fileName, mimeType = 'application/pdf') {
      try {
        const binaryString = atob(base64String);
        const bytes = new Uint8Array(binaryString.length);
        
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        const blob = new Blob([bytes], { type: mimeType });
        const blobUrl = URL.createObjectURL(blob);
        
        const newWindow = window.open(blobUrl, '_blank');
        
        if (!newWindow) {
          this.downloadFileDirectly(blob, fileName);
        } else {
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
          }, 60000);
        }
      } catch (error) {
        console.error('Ошибка при открытии файла:', error);
        this.openFileViaDataURL(base64String, fileName, mimeType);
      }
    },

    /**
     * Альтернативный способ открытия файла через data URL
     */
    openFileViaDataURL(base64String, fileName, mimeType) {
      const dataUrl = `data:${mimeType};base64,${base64String}`;
      const newWindow = window.open(dataUrl, '_blank');
      
      if (!newWindow) {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = fileName;
        link.target = '_blank';
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },

    /**
     * Прямое скачивание файла
     */
    downloadFileDirectly(blob, fileName) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      link.href = url;
      link.download = fileName;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 100);
    },

    /**
     * Обработка ошибок загрузки файла
     */
    handleFileError(error) {
      let errorMessage = this.$t("applications.applicationDetail.downloadError");
      
      if (error.response?.status === 404) {
        errorMessage = this.$t("applications.applicationDetail.fileNotFound");
      }
      
      showToast(this, {
        text: errorMessage,
        status: "error",
        duration: 3000,
      });
    },

    /**
     * Извлечение имени файла из пути
     */
    extractFileName(path) {
      if (!path) return 'document.pdf';
      const parts = path.split('/');
      return parts[parts.length - 1] || 'document.pdf';
    },

    /**
     * Проверка состояния загрузки файла
     */
    isFileLoading(fileType) {
      return this.fileLoading[fileType] || false;
    },

    /**
     * Подтверждение действия с заявкой
     */
    confirmApplicationAction(action) {
      this.confirmDialog = {
        visible: true,
        action,
      };
    },

    /**
     * Обработка подтвержденного действия
     */
    async handleConfirm() {
      this.confirmDialog.visible = false;
      this.isSubmitLoading = true;

      try {
        await fetchDeleteApplication(this.applicationInfo.id);
        
        const toastMessage = this.confirmDialog.action === "delete"
          ? this.$t("toastText.applicationDeleted")
          : this.$t("toastText.applicationWithdrawn");
        
        showToast(this, {
          text: toastMessage,
          status: "success",
          duration: 3000,
        });
        
        this.$router.back();
      } catch (error) {
        console.error("Ошибка при обработке заявки:", error);
        
        const errorMessage = error.response?.data?.message 
          ? `${this.$t("toastText.errorData")}: ${error.response.data.message}`
          : this.$t("toastText.errorData");
        
        showToast(this, {
          text: errorMessage,
          status: "error",
          duration: 3000,
        });
      } finally {
        this.isSubmitLoading = false;
      }
    },

    /**
     * Инициализация данных страницы
     */
    async initializePageData() {
      if (this.isLoading) return;

      this.isLoading = true;
      const { studentId, applicationId } = this.$route.params;

      try {
        await Promise.all([
          this.fetchApplicationInfo(applicationId),
        ]);
      } catch (error) {
        console.error("Ошибка при загрузке данных:", error);
        
        showToast(this, {
          text: `${error.response.data.message || error}`,
          status: "error",
          duration: 3000,
        });
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * Форматирование даты
     */
    formatDateToMonthYear(dateStr) {
      if (!dateStr) return "";
      try {
        const date = parseISO(dateStr);
        return format(date, "dd.MM.yyyy");
      } catch (error) {
        return dateStr;
      }
    },
  },
};
</script>

<style scoped>
.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  height: 60px;
}

.btn.accept-btn {
  height: 46px !important;
  margin-top: 20px;
  background: #f79824;
  font-size: 16px;
  color: white;
  width: 100%;
}

.file-btn {
  text-transform: none;
  font-weight: normal;
  padding: 0 !important;
  margin: 0;
  min-width: 0;
  color: #007bff;
  text-decoration: underline;
}

.file-btn:hover {
  text-decoration: underline;
  background: transparent !important;
}

.file-btn:disabled {
  color: #aaa !important;
  text-decoration: none;
}

.acts-v-chip {
  font-weight: bold;
  cursor: pointer;
}

.acts-wrapper,
h2,
h3 {
  font-family: "Montserrat Alternates", sans-serif;
}

.acts-inner-wrapper {
  display: flex;
  justify-content: space-between;
  gap: 40px;
  font-family: "Montserrat", sans-serif;
}

.acts-detail {
  flex: 2;
  min-width: 300px;
}

.acts-list {
  flex: 1;
  min-width: 300px;
}

.acts-v-list {
  padding: 0;
}

.section-title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 8px;
}

.section-subtitle {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 16px;
}

.info-card {
  border-radius: 8px;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.price-card {
  background: #fff;
  padding: 24px;
  border-radius: 8px;
  font-weight: 700;
  color: #000000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.group-name-title {
  font-weight: bold;
  font-size: 24px;
  margin-bottom: 16px;
}

.sub-group-name-title {
  font-size: 16px;
  margin-bottom: 16px;
}

.share-btn {
  color: #0056b3 !important;
  text-decoration: underline;
  font-size: 14px;
  margin-top: 16px;
  width: 100%;
}

.acts-list-card {
  margin-top: 16px;
  border-radius: 8px;
  overflow: hidden;
}

.acts-item {
  border-radius: 8px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.acts-item:hover {
  background-color: #f5f5f5;
}

.view-all {
  text-align: center;
  margin-top: 16px;
}

.view-all-btn {
  color: #007bff !important;
  text-decoration: underline;
  font-size: 14px;
  font-weight: bold;
}

@media (max-width: 1024px) {
  .acts-inner-wrapper {
    flex-direction: column;
    gap: 24px;
  }

  .acts-detail,
  .acts-list {
    width: 100%;
  }
}

@media (max-width: 600px) {
  .acts-wrapper {
    padding: 16px;
  }

  .section-title {
    font-size: 18px;
  }

  .section-subtitle {
    font-size: 16px;
  }

  .price-card {
    padding: 16px;
  }

  .btn.accept-btn {
    font-size: 14px;
    height: 40px !important;
  }
}

@media (max-width: 480px) {
  .section-title {
    text-align: center;
  }

  .section-subtitle {
    text-align: center;
  }
}
</style>